/**
 * REVIEW FP-004 — Shared filter-dropdown reference-data queries.
 *
 * Many list pages (machines, maker logsheets, sites, etc.) render filter
 * dropdowns backed by paginated reference endpoints
 * (`/api/sites?status=active`, `/api/machine-types?status=active`,
 * `/api/machines?status=active`). These are effectively static — they change
 * once a quarter, not every 30 seconds.
 *
 * Each consuming page previously declared its own ad-hoc `useQuery`
 * with the default `staleTime: 30_000`. Result: every 30-second nav between
 * list pages re-fetched every dropdown's full first page. With ~5 list pages
 * touching the same dropdowns, this is ~30 wasted requests per shift.
 *
 * These hooks centralise the pattern with:
 *  - `staleTime: 5 * 60_000`   (5 minutes — well below the real change cadence)
 *  - `gcTime:    30 * 60_000`  (30 minutes — keep the cached page warm across
 *                                long-shift nav patterns; the data is tiny)
 *  - A stable, narrow `queryKey` so two consumers share a single cache slot
 *    regardless of which page mounts first.
 *
 * Each returns a first-page response — `pageSize: 100` for the dropdown-only
 * hooks, 200 where the list also backs the user-scope multi-select (a reference
 * row missing from the page is a grant an admin cannot make). Callers read
 * `.data?.items` exactly as before.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/error';
import { listMachineTypesRequest } from '@/services/machine-types';
import { listMachinesRequest } from '@/services/machines';
import { listRolesRequest } from '@/services/roles';
import { listSitesRequest } from '@/services/sites';
import { listUsersRequest } from '@/services/users';
import { listWorkOrdersRequest } from '@/services/work-orders';
import type { MachineTypeListItem } from '@/types/machine-types';
import type { MachineListItem } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';
import type { UserListItem } from '@/types/users';
import type { WorkOrderListItem } from '@/types/work-orders';

const REF_STALE_MS = 5 * 60_000;
const REF_GC_MS = 30 * 60_000;

export function useActiveSitesForFilterQuery(): UseQueryResult<
  PaginatedResult<SiteListItem>,
  ApiError
> {
  return useQuery<PaginatedResult<SiteListItem>, ApiError>({
    queryKey: ['sites', 'filter-options', 'active'] as const,
    // 200, not 100: this now also backs the user-scope multi-select, where a site
    // missing from the page is a site an admin cannot grant. Filter dropdowns are
    // unaffected by the larger page — the payload is a few KB.
    queryFn: () => listSitesRequest({ status: 'active', page: 1, pageSize: 200 }),
    staleTime: REF_STALE_MS,
    gcTime: REF_GC_MS,
  });
}

/**
 * Active work orders for the user-scope multi-select. A work-order grant is
 * UNION-additive to a user's site grants — it exposes that work order's records
 * wherever they live, including at sites the user does not hold.
 */
export function useActiveWorkOrdersForFilterQuery(): UseQueryResult<
  PaginatedResult<WorkOrderListItem>,
  ApiError
> {
  return useQuery<PaginatedResult<WorkOrderListItem>, ApiError>({
    queryKey: ['work-orders', 'filter-options', 'active'] as const,
    // Deliberately unfiltered by status. The backend accepts an assignment to any
    // work order that is not soft-deleted, and both ends of the lifecycle are real
    // cases — pre-staging access on a Draft WO, retaining it on a Completed one.
    // Filtering to Active here would hide options the server would happily accept.
    queryFn: () => listWorkOrdersRequest({ page: 1, pageSize: 200 }),
    staleTime: REF_STALE_MS,
    gcTime: REF_GC_MS,
  });
}

export function useActiveMachineTypesForFilterQuery(): UseQueryResult<
  PaginatedResult<MachineTypeListItem>,
  ApiError
> {
  return useQuery<PaginatedResult<MachineTypeListItem>, ApiError>({
    queryKey: ['machine-types', 'filter-options', 'active'] as const,
    queryFn: () => listMachineTypesRequest({ status: 'active', page: 1, pageSize: 100 }),
    staleTime: REF_STALE_MS,
    gcTime: REF_GC_MS,
  });
}

export function useActiveMachinesForFilterQuery(): UseQueryResult<
  PaginatedResult<MachineListItem>,
  ApiError
> {
  return useQuery<PaginatedResult<MachineListItem>, ApiError>({
    queryKey: ['machines', 'filter-options', 'active'] as const,
    queryFn: () => listMachinesRequest({ status: 'active', page: 1, pageSize: 100 }),
    staleTime: REF_STALE_MS,
    gcTime: REF_GC_MS,
  });
}

/**
 * Active users assigned to the Checker role — for the Approver queue's
 * "Checker" filter dropdown. Chains roles list -> users list to resolve the
 * Checker roleId at runtime (roles are seeded with stable names but the
 * numeric IDs are not contractually fixed).
 */
export function useActiveCheckersForFilterQuery(): UseQueryResult<
  readonly UserListItem[],
  ApiError
> {
  return useQuery<readonly UserListItem[], ApiError>({
    queryKey: ['users', 'filter-options', 'role:Checker'] as const,
    queryFn: async () => {
      const roles = await listRolesRequest({ status: 'active', page: 1, pageSize: 100 });
      const checker = roles.items.find((r) => r.name === 'Checker');
      if (checker === undefined) return [];
      const users = await listUsersRequest({
        roleId: checker.id,
        status: 'active',
        page: 1,
        pageSize: 100,
      });
      return users.items;
    },
    staleTime: REF_STALE_MS,
    gcTime: REF_GC_MS,
  });
}
